import React, { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { styled, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import { ErrorBoundary } from 'react-error-boundary'
import { Helmet } from 'react-helmet-async'
import TopNav from './components/common/TopNavigation'
import BottomNav from './components/common/BottomNavigation'
import OpsotFooter from './components/common/OpsotFooter'
import { AuthProvider } from './AuthProvider'
import PrivateRoute from './PrivateRoute'
import { SessionTimeout } from './components/functional/SessionTimeout'
import useFilterResetOnNavigation from './hooks/useFilterResetOnNavigation'
import useRedirect from './hooks/useRedirect'
import useTrackExternalLinks from './hooks/useTrackExternalLinks'
import useTrackFileLinks from './hooks/useTrackFileLinks'
import { useGlobalStore } from './services/store/store'
import usePreloadToolkits from './hooks/usePreloadToolkits'
import { removeLocalStorage } from './utils'
import useNetworkStatus from './hooks/useNetworkStatus'

const Login = lazy(() => import('./pages/Login'))
const ValidateAuthCode = lazy(() => import('./pages/ValidateAuthCode'))
const Home = lazy(() => import('./pages/Home'))
const Search = lazy(() => import('./pages/Search'))
const NotFound = lazy(() => import('./pages/NotFound'))
const UnknownError = lazy(() => import('./pages/UnknownError'))
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'))
const Toolkits = lazy(() => import('./pages/Toolkits'))
const SingleToolkit = lazy(() => import('./pages/SingleToolkit'))
const AccessibilityStatement = lazy(
	() => import('./pages/AccessibilityStatement')
)
const ContentDisclaimer = lazy(() => import('./pages/ContentDisclaimer'))
const CopyrightStatement = lazy(() => import('./pages/CopyrightStatement'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const DraftToolkits = lazy(() => import('./pages/DraftToolkits'))
const DraftSingleToolkit = lazy(() => import('./pages/DraftSingleToolkit'))

const MainContent = styled(Box)(({ theme }) => ({
	background: theme.palette.background.main,
	fontFamily: theme.typography.fontFamily
}))
const App = () => {
	const location = useLocation()
	const theme = useTheme()
	useEffect(() => {
		const preloadRoutes = [
			import('./pages/Login'),
			import('./pages/ValidateAuthCode'),
			import('./pages/Home'),
			import('./pages/Search'),
			import('./pages/NotFound'),
			import('./pages/UnknownError'),
			import('./pages/CookiePolicy'),
			import('./pages/Toolkits'),
			import('./pages/SingleToolkit'),
			import('./pages/ContentDisclaimer'),
			import('./pages/CopyrightStatement'),
			import('./pages/PrivacyPolicy')
		]
		preloadRoutes.forEach((route) => route.catch(() => {})) // Ignore errors
	}, [])
	const setFromSingleToolkitToToolkitsPage = useGlobalStore(
		(state) => state.setFromSingleToolkitToToolkitsPage
	)
	const previousRoute = useGlobalStore((state) => state.previousRoute)
	const currentRoute = useGlobalStore((state) => state.currentRoute)
	const isAdmin = useGlobalStore((state) => state.isAdmin)

	useEffect(() => {
		setFromSingleToolkitToToolkitsPage(
			(previousRoute === '/toolkits' &&
				/^\/toolkit\/\d+$/.test(currentRoute)) ||
				(currentRoute === '/toolkits' && /^\/toolkit\/\d+$/.test(previousRoute))
		)
	}, [previousRoute, currentRoute, setFromSingleToolkitToToolkitsPage])

	useEffect(() => {
		if (
			previousRoute === '/search' &&
			!currentRoute.includes('/toolkit/') &&
			!previousRoute.includes('/toolkit/') &&
			currentRoute !== '/search'
		) {
			removeLocalStorage('searchResults')
			removeLocalStorage('question')
			removeLocalStorage('additionalSearchResult')
			removeLocalStorage('additionalQuestion')
			removeLocalStorage('searchScrollPosition')
		}
	}, [previousRoute, currentRoute])

	const gtmId = import.meta.env.VITE_GTM_ID
	useFilterResetOnNavigation()
	useRedirect()
	useTrackExternalLinks()
	useTrackFileLinks()
	usePreloadToolkits()
	useNetworkStatus(theme)
	return (
		<>
			<Helmet>
				<noscript>
					{`<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}
				</noscript>
			</Helmet>
			<AuthProvider>
				<Box
					component="main"
					sx={{
						overflowX: 'hidden',
						marginBlockEnd: { xs: '0.4375rem', sm: '10.0625rem', md: 0 }
					}}
				>
					<TopNav />
					<MainContent
						sx={{
							paddingTop: { xs: '6.025rem', xl: '8.9375rem' }
						}}
					>
						<Suspense fallback={<div>Loading...</div>}>
							{<SessionTimeout sessionTime={60 * 60 * 1000} /> /* 1 hour */}
							<ErrorBoundary
								FallbackComponent={UnknownError}
								resetKeys={[location.pathname]}
							>
								<Routes>
									<Route path="/login" element={<Login />} />
									<Route
										path="/validate-auth-code"
										element={<ValidateAuthCode />}
									/>
									<Route
										path="/accessibility-statement"
										element={<AccessibilityStatement />}
									/>
									<Route
										path="/copyright-statement"
										element={<CopyrightStatement />}
									/>
									<Route
										path="/content-disclaimer"
										element={<ContentDisclaimer />}
									/>
									<Route path="/privacy-policy" element={<PrivacyPolicy />} />
									<Route path="/cookie-policy" element={<CookiePolicy />} />
									<Route element={<PrivateRoute />}>
										<Route path="/" element={<Home />} />
										<Route path="/search" element={<Search />} />
										<Route path="/toolkits" element={<Toolkits />} />
										<Route path="/toolkit/:nnid" element={<SingleToolkit />} />
										{isAdmin && (
											<>
												<Route
													path="/draft-toolkits"
													element={<DraftToolkits />}
												/>
												<Route
													path="/draft-toolkit/:nnid"
													element={<DraftSingleToolkit />}
												/>
											</>
										)}
										<Route path="*" element={<NotFound />} />
									</Route>
								</Routes>
							</ErrorBoundary>
						</Suspense>
					</MainContent>
					<BottomNav />
					<OpsotFooter />
				</Box>
			</AuthProvider>
		</>
	)
}

export default App
